/**
 * Design Tokens — single source of truth for the Vendor Portal's premium brand
 * identity. One tuned palette (deep indigo→violet brand + cyan accent); no
 * runtime theme switching.
 *
 * Styling contract (the "three-channel rule"), because the host Tailwind config
 * (apps/core-shell/tailwind.config.ts) ships only the default palette + Inter and
 * has NO custom brand colors:
 *   1. Brand fills / gradients / glass / colored shadows → inline `style` from
 *      these tokens (Tailwind can't emit `bg-brand-600` without config changes).
 *   2. Semantic status colors → static *literal* Tailwind classes
 *      (e.g. `bg-emerald-50 text-emerald-700 ring-emerald-200`) so JIT picks
 *      them up. Hex equivalents below are for inline needs (dots, charts).
 *   3. Structure (spacing / radius / layout / neutral text+bg) → Tailwind
 *      utilities, composed via `cn()`.
 *
 * `lib/themes.ts` builds its single brand `ThemeColors` FROM this module, so the
 * legacy `useTheme().theme.colors.*` shim and the new primitives resolve to the
 * exact same hex — tune a color once here.
 */

// ── Brand ──────────────────────────────────────────────────────────────────
export const brand = {
  primary: '#4F46E5', // indigo-600
  primaryHover: '#4338CA', // indigo-700
  primaryDark: '#312E81', // indigo-900 (hero gradient tail)
  primaryLight: '#EEF2FF', // indigo-50  (tints, active nav bg)

  secondary: '#475569', // slate-600
  secondaryHover: '#334155', // slate-700
  secondaryLight: '#F1F5F9', // slate-100

  accent: '#06B6D4', // cyan-500
  accentHover: '#0891B2', // cyan-600
  accentLight: '#ECFEFF', // cyan-50
} as const;

// ── Semantic status ──────────────────────────────────────────────────────────
// base = solid color (dots/charts), soft = tinted bg, text = on-soft text,
// ring = soft border. The matching literal Tailwind classes live in the
// Badge/StatusPill primitive (channel #2 above).
export const status = {
  success: { base: '#10B981', soft: '#ECFDF5', text: '#047857', ring: '#A7F3D0' }, // emerald
  warning: { base: '#F59E0B', soft: '#FFFBEB', text: '#B45309', ring: '#FDE68A' }, // amber
  error: { base: '#EF4444', soft: '#FEF2F2', text: '#B91C1C', ring: '#FECACA' }, // red
  info: { base: '#3B82F6', soft: '#EFF6FF', text: '#1D4ED8', ring: '#BFDBFE' }, // blue
  neutral: { base: '#64748B', soft: '#F8FAFC', text: '#334155', ring: '#E2E8F0' }, // slate
} as const;

export type StatusTone = keyof typeof status;

// ── Surfaces & neutrals ──────────────────────────────────────────────────────
export const surface = {
  page: '#F6F7FB', // app background (cool off-white)
  card: '#FFFFFF',
  border: '#E5E7EB',
  borderStrong: '#CBD5E1',
  muted: '#F1F5F9',
} as const;

// ── Premium gradients (used as inline `background`) ───────────────────────────
export const gradient = {
  brand: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 60%, #312E81 100%)',
  brandSoft: 'linear-gradient(135deg, #EEF2FF 0%, #FFFFFF 100%)',
  hero: 'linear-gradient(135deg, #4F46E5 0%, #312E81 70%, #0F172A 100%)',
  // Licence Passport hero (Display Devices). Teal instead of the indigo
  // `hero` above: that gradient's near-black tail read too dark on the card.
  licenceHeroLight: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 55%, #BAE6FD 100%)',
  // Light counterpart, used by the hero's `light` skin (dark text on a pale
  // card). Swap either line to re-colour the card; nothing else changes.
  accent: 'linear-gradient(135deg, #06B6D4 0%, #4F46E5 100%)',
  success: 'linear-gradient(135deg, #10B981 0%, #0D9488 100%)',
  danger: 'linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)',
} as const;

// ── Glass surfaces (overlays atop gradient heroes) ────────────────────────────
import type { CSSProperties } from 'react';
export const glass: Record<'light' | 'dark', CSSProperties> = {
  light: {
    background: 'rgba(255,255,255,0.12)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.22)',
  },
  dark: {
    background: 'rgba(15,23,42,0.55)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
};

// ── Elevation / soft shadows ─────────────────────────────────────────────────
export const shadow = {
  xs: '0 1px 2px rgba(16,24,40,0.05)',
  sm: '0 1px 3px rgba(16,24,40,0.08), 0 1px 2px rgba(16,24,40,0.04)',
  md: '0 4px 12px rgba(16,24,40,0.08), 0 2px 4px rgba(16,24,40,0.04)',
  lg: '0 12px 28px rgba(16,24,40,0.10), 0 4px 8px rgba(16,24,40,0.05)',
  brand: '0 8px 24px rgba(79,70,229,0.25)', // colored glow for primary CTAs
} as const;

// ── Radius / spacing scales (mirror Tailwind so classes stay usable) ──────────
export const radius = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  '2xl': '1.25rem',
  full: '9999px',
} as const;

export const space = {
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
} as const;

// ── Typography (Tailwind class presets) ──────────────────────────────────────
export const typography = {
  display: 'text-3xl md:text-4xl font-black tracking-tight',
  h1: 'text-2xl font-bold tracking-tight',
  h2: 'text-lg font-bold',
  sectionLabel: 'text-[11px] font-bold uppercase tracking-[0.1em]',
  body: 'text-sm',
  caption: 'text-xs text-slate-500',
} as const;

export const tokens = {
  brand,
  status,
  surface,
  gradient,
  glass,
  shadow,
  radius,
  space,
  typography,
} as const;

export default tokens;
